import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AccountLayout from "../components/AccountLayout.jsx";
import { ProgramsPageSkeleton } from "../components/loading/SkeletonBlocks.jsx";
import { getCoachProgramListings } from "../lib/appwrite.js";
import { useAuthUserId } from "../hooks/useAuthUserId.js";
import { isListingPublished, priceLabelForListing } from "../lib/listingPricing.js";

const DEFAULT_COVER =
  "linear-gradient(135deg, rgba(255,118,25,0.55) 0%, rgba(245,158,11,0.45) 100%)";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function CopyLinkButton({ listingId }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(`${window.location.origin}/programs/${listingId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1.5 rounded-[10px] border border-hc-surface-border-strong px-3 py-1.5 text-xs font-medium text-hc-text-muted transition hover:bg-white/5"
    >
      <i
        className={`${copied ? "icon-check" : "icon-link"} leading-none`}
        aria-hidden="true"
      />
      {copied ? "Copied!" : "Copy link"}
    </button>
  );
}

export default function ProgramsPage() {
  const { userId } = useAuthUserId();
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const listings = await getCoachProgramListings(userId);
        if (!cancelled) setPrograms(listings);
      } catch (err) {
        if (!cancelled) setError(err?.message ?? "Failed to load your programs.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const published = programs.filter((p) => isListingPublished(p));
  const drafts = programs.filter((p) => !isListingPublished(p));
  const publishedPct = programs.length
    ? Math.round((published.length / programs.length) * 100)
    : 0;

  if (error) {
    return (
      <AccountLayout>
        <div className="rounded-2xl border border-hc-error/30 bg-hc-error/10 px-6 py-8 text-center">
          <p className="text-sm font-semibold text-hc-text">Could not load your programs</p>
          <p className="mt-1.5 text-xs text-hc-error">{error}</p>
        </div>
      </AccountLayout>
    );
  }

  if (isLoading) {
    return (
      <AccountLayout>
        <ProgramsPageSkeleton />
      </AccountLayout>
    );
  }

  return (
    <AccountLayout>
      <section className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-[14px] border border-hc-surface-border bg-hc-surface p-4">
          <p className="text-xs text-hc-text-dim">Total programs</p>
          <p className="mt-1 text-2xl font-bold tracking-[-0.45px] text-hc-text">
            {programs.length}
          </p>
          <div className="mt-3 flex flex-wrap gap-1">
            {programs.map((p) => (
              <div
                key={p.id}
                className="size-2 rounded-[2px]"
                style={{
                  backgroundColor: isListingPublished(p)
                    ? "#22c55e"
                    : "rgba(255,255,255,0.08)",
                }}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-hc-text-dim">
            {drafts.length} draft{drafts.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="rounded-[14px] border border-hc-surface-border bg-hc-surface p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-hc-text-dim">Published</p>
              <p className="mt-1 text-2xl font-bold tracking-[-0.45px] text-hc-text">
                {published.length}
                <span className="text-sm font-normal text-hc-text-muted">
                  /{programs.length}
                </span>
              </p>
            </div>
            <div
              className="mt-0.5 size-12 shrink-0 rounded-full"
              style={{
                background: `conic-gradient(#ff7619 ${publishedPct}%, rgba(255,255,255,0.08) ${publishedPct}%)`,
                WebkitMask: "radial-gradient(transparent 44%, black 45%)",
                mask: "radial-gradient(transparent 44%, black 45%)",
              }}
            />
          </div>
          <p className="mt-3 text-xs text-hc-text-dim">
            {publishedPct}% of programs live
          </p>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface">
        <div className="border-b border-hc-surface-border px-5 py-3.5">
          <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
            All programs
          </h2>
        </div>

        {programs.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm font-medium text-hc-text">No programs yet</p>
            <p className="mt-1 text-xs text-hc-text-dim">
              Create and publish a program in the app, then set pricing on its share page.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-hc-surface-border/80">
            {programs.map((program) => (
              <li
                key={program.id}
                className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="size-10 shrink-0 rounded-[10px]"
                    style={{ background: DEFAULT_COVER }}
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/programs/${program.id}`}
                        className="text-sm font-medium tracking-[-0.15px] text-hc-text hover:text-hc-primary transition"
                      >
                        {program.title}
                      </Link>
                      {isListingPublished(program) ? (
                        <span className="rounded-full border border-hc-success/35 bg-hc-success-muted px-2 py-0.5 text-xs font-medium text-hc-success">
                          Published
                        </span>
                      ) : (
                        <span className="rounded-full border border-hc-surface-border-strong bg-hc-surface-strong px-2 py-0.5 text-xs font-medium text-hc-text-dim">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-hc-text-dim">
                      {priceLabelForListing(program)}
                      {program.createdAt && (
                        <>
                          <span className="mx-1.5 opacity-40">·</span>
                          Added {formatDate(program.createdAt)}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {isListingPublished(program) ? (
                  <CopyLinkButton listingId={program.id} />
                ) : (
                  <span className="rounded-[10px] border border-hc-surface-border px-3 py-1.5 text-xs text-hc-text-dim">
                    Not published
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </AccountLayout>
  );
}
